import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import AppBar from '@material-ui/core/AppBar';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import useUserContext from 'Context/UserContext';
import history from 'State/history';
import { addSpacesAndCapitalize } from 'Helpers/Formats';
import { pickComponentAndProps } from 'Helpers/Affordances';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import usePathIsMobileFeed from 'Hooks/usePathIsMobileFeed';
import relationships from 'Consts/Relationships';
import HeaderActions from 'Components/UI/HeaderActions';
import useMobileLayout from 'Hooks/useMobileLayout';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';
import MemberAlertsTooltip from 'Components/AdvisorApp/Header/MemberAlertsTooltip';
import { PlanningVersionLozengePicker } from 'Components/AdvisorApp/Header/lozenges/PlanningVersionLozengePicker/PlanningVersionLozengePicker';
import Tag from 'Components/AdvisorApp/Viewport/Household/LandingPage/Tag';
import { EstatePlanningLozenge } from 'Components/AdvisorApp/Header/lozenges/EstatePlanningLozenge';
import { FDStack } from '@facet-wealth/design';
import { useLayoutContext } from 'Context/LayoutContext';
import { TaxFilingLozenge } from 'Components/AdvisorApp/Header/lozenges/TaxFilingLozenge';
import OutOfOfficeComponentPicker from './OutOfOfficeComponentPicker';
import ClientSearchBar from './ClientSearchBar';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.common.white,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.facet.border,
    zIndex: 200,
    position: 'sticky',
    top: 0,
    paddingLeft: '0px',
    height: theme.heights.advisorNavigationHeight,
  },
  rootMobileFeed: {
    paddingLeft: '0px', // Removes padding (sidebar is not visible for mobile feed)
    overflow: 'hidden',
  },
  toolbar: {
    minHeight: theme.heights.advisorNavigationHeight,
  },
  breadcrumbsText: {
    ...theme.typography.h4,
    color: theme.palette.facet.accentBlue2,
    fontWeight: theme.typography.fontWeightRegular,
  },
  breadcrumbsListItem: {
    maxWidth: theme.dimensions.lg,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  breadcrumbsLink: {
    color: 'inherit',
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  },
  outOfOfficeBanner: {
    backgroundColor: theme.palette.facet.primaryBlue,
    display: 'flex',
    justifyContent: 'center',
    color: 'white',
  },
  bannerButton: {
    padding: '0 1em',
    background: 'none',
    textDecoration: 'underline',
  },
  headerColumn: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    '&:first-child': {
      justifyContent: 'flex-start',
    },
    '&:last-child': {
      justifyContent: 'flex-end',
    },
  },
  memberAlert: {
    marginTop: 'auto',
    marginBottom: 'auto',
    padding: theme.spacing(1),
  },
  tags: {
    marginTop: 'auto',
    marginBottom: 'auto',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
  },
}));

function getBreadCrumbDisplay(primaryClient, householdId, pathParts) {
  const { search } = history.location;

  switch (pathParts[0]) {
    case 'administrator':
      return 'Dashboard';
    case 'account-support':
      return 'Account Support';
    case 'identity-verification-reviews':
      return 'Identity Verification Reviews';
    case 'mission-control':
      return 'Mission Control';
    case 'client-experience-saves':
      return 'Saves';
    case 'account-requests':
      return 'Account Requests';
    case 'account-management':
      return 'Account Management';
    case 'managed-account':
    case 'managed-account-page':
      return 'Managed Account';
    case 'client-experience':
      return 'Client Experience';
    case 'linking-attempts':
      return 'Linking Attempts';
    case 'managed-account-cleanup-households':
      return 'Managed Account Cleanup';
    case 'managed-account-model-update-request-page':
      return 'Model Updates';
    case 'fq':
      return 'Facet Quiz Admin';
    case 'planner-transition-household':
      return 'Planner Transitions';
    case 'transitions-dashboard-page':
      return 'Dashboard';
    case 'meeting-summary':
      if (search.includes('householdId') && primaryClient) {
        return `Meeting Summary > ${primaryClient.attr('nickname') || primaryClient.attr('firstName')} ${primaryClient.attr(
          'lastName',
        )} [${householdId}]`;
      }
      return 'Meeting Summary';
    case 'household':
      if (householdId) {
        let display = '';
        if (primaryClient) {
          display += `${primaryClient.attr('nickname') || primaryClient.attr('firstName')} ${primaryClient.attr('lastName')}`;
        } else {
          display += 'Household';
        }
        return `${display} [${householdId}]`;
      }
      break;
    case 'feed':
      return 'Messages';
    case 'household-access-justification':
      return 'Household Access';
    case 'secureFiles':
      return 'Files';
    case 'my-account':
      return 'My Account';
    case 'performance-commentary-time-period-page':
      return 'Performance Commentary Time Periods';
    case 'performance-commentary-page':
      return 'Performance Commentaries';
    case 'what-if':
      if (search.includes('householdId') && primaryClient) {
        return `Planning Engine > ${primaryClient.attr('nickname') || primaryClient.attr('firstName')} ${primaryClient.attr(
          'lastName',
        )} [${householdId}]`;
      }
      return 'Planning Engine';
    case 'security-settings':
      return 'Security Settings';
    case 'assumptions-admin':
      return 'Global Facet Plan Assumptions';
    case 'global-assumptions-by-date':
      return 'Global Facet Plan Assumptions';
    case 'guided-household-review':
    case 'pm2-journey':
    case 'roadmap-explorer':
      return 'Path Explorer';
    case 'wizard':
      switch (pathParts[1]) {
        case 'guidedHouseholdReview':
          return 'Path Explorer';
        /* no default */
      }
    /* no default */
  }

  return addSpacesAndCapitalize(pathParts[0]);
}

/* Add proper breadcrumbs for page context links
 */
function augmentPageContextInBreadcrumbs(breadcrumbs, pathParts, primaryClient, classes) {
  // extract page self link
  const windowParams = new URLSearchParams(window.location.search);
  const pageSelfLink = windowParams.get('pageSelfLink');
  if (!pageSelfLink) return;

  const pageSelfLinkURL = new URL(pageSelfLink);

  // add household breadcrumb if it does not exist
  const householdId = pageSelfLinkURL.searchParams.get('householdId');
  const doesHouseholdPathPartAlreadyExist = pathParts.includes('household');
  if (householdId && !doesHouseholdPathPartAlreadyExist) {
    const linkTo = '/administrator/household/landing';
    breadcrumbs.push(
      <Link
        key={`link.${breadcrumbs.length}`} // eslint-disable-line react/no-array-index-key
        className={classes.breadcrumbsLink}
        to={`${linkTo}?householdId=${householdId}`}
        href={linkTo}
        test="advisorSuite"
      >
        {getBreadCrumbDisplay(primaryClient, householdId, ['household'])}
      </Link>,
    );
  }

  // add page context specific breadcrumb
  const pageSelfLinkURLPathParts = pageSelfLinkURL.pathname
    .split('/')
    .filter(v => v !== '');
  const pageContextRoot = pageSelfLinkURLPathParts[0];
  if (pageContextRoot) {
    breadcrumbs.push(
      <Typography
        key={`link.${breadcrumbs.length}`} // eslint-disable-line react/no-array-index-key
        className={classNames(classes.breadcrumbsText)}
      >
        {getBreadCrumbDisplay(primaryClient, householdId, pageSelfLinkURLPathParts)}
      </Typography>,
    );
  }
}

function getBreadcrumbs(props, primaryClient, householdId, classes) {
  const pathParts = history.location.pathname
    .split('/')
    .filter(v => !['', 'edit'].includes(v));

  const breadcrumbs = pathParts.map((v, i) => {
    // we will process 'page' path part for page contexts later
    if (v === 'page') return 'page';

    let linkTo = '';

    for (let index = 0; index <= i; index++) {
      linkTo = `${linkTo}/${pathParts[index]}`;
      if (
        v === 'household'
        && pathParts[index] === 'household'
        && history.location.search
      ) {
        linkTo += '/landing';
      }
    }

    if (i === pathParts.length - 1) {
      return (
        <Typography
          key={`link.${i}`} // eslint-disable-line react/no-array-index-key
          className={classNames(classes.breadcrumbsText)}
        >
          {getBreadCrumbDisplay(primaryClient, householdId, [v])}
        </Typography>
      );
    }

    return (
      <Link
        key={`link.${i}`} // eslint-disable-line react/no-array-index-key
        className={classes.breadcrumbsLink}
        to={i > 0 ? linkTo + history.location.search : linkTo}
        href={linkTo}
        test="advisorSuite"
      >
        {getBreadCrumbDisplay(primaryClient, householdId, [v])}
      </Link>
    );
  });

  // process page breadcrumb path
  const pageBreadcrumbIndex = breadcrumbs.indexOf('page');
  const isPageLastBreadcrumb = pageBreadcrumbIndex === breadcrumbs.length - 1;
  // remove page path part
  if (pageBreadcrumbIndex > -1) breadcrumbs.splice(pageBreadcrumbIndex, 1);
  // process page context for breadcrumbs
  if (isPageLastBreadcrumb) {
    augmentPageContextInBreadcrumbs(breadcrumbs, pathParts, primaryClient, classes);
  }

  return breadcrumbs;
}

const Header = (props) => {
  const isMobile = useMobileLayout();
  const classes = useStyles();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const { isFocusedLayout } = useLayoutContext();

  const {
    getPrimaryClient,
    householdId,
    clearHousehold,
    householdResource,
  } = useSimpleHousehold();
  const { agentResource, refreshAgent } = useUserContext();
  const primaryClient = getPrimaryClient();

  const [outOfOfficeModalOpen, setOutOfOfficeModalOpen] = useState(false);
  const isOutOfOffice = Boolean(agentResource?.attr('outOfOffice') === 'true');

  const pathIsMobileFeed = usePathIsMobileFeed();

  const disableOutOfOffice = async () => {
    try {
      agentResource.attr('outOfOffice', false);
      await agentResource.submit();
      const [outOfOfficeResource] = await agentResource.relationship(fr.relationships.outOfOffice.name).link('related').get();
      if (outOfOfficeResource) {
        await outOfOfficeResource.delete();
      }
      refreshAgent();
      displaySuccess('Out of office status updated.', 5000);
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to update out of office status.');
    }
  };

  const returnFromOutOfOffice = useCallback(disableOutOfOffice, [agentResource, refreshAgent]);

  const openOutOfOfficeModal = () => {
    if (isOutOfOffice) {
      returnFromOutOfOffice();
    } else {
      setOutOfOfficeModalOpen(true);
    }
  };

  const handleOutOfOfficeBannerClick = () => {
    returnFromOutOfOffice();
  };

  const handleOutOfOfficeModalClose = () => {
    setOutOfOfficeModalOpen(false);
    refreshAgent();
  };


  const OutOfOfficeModalComponent = () => (
    <OutOfOfficeComponentPicker
      onClose={handleOutOfOfficeModalClose}
    />
  );

  const OutOfOfficeBanner = () => (
    <div className={classes.outOfOfficeBanner}>
      Out of office enabled.
      <button className={classes.bannerButton} type="button" onClick={handleOutOfOfficeBannerClick}>Click to dismiss</button>
    </div>
  );

  const { component: OutOfOfficeBannerComponent, props: outOfOfficeBannerProps } = pickComponentAndProps(agentResource, {
    [relationships.default]: [OutOfOfficeBanner, {}],
  });

  const showAlertButton = householdResource !== null && householdResource?.attr('memberAlerts').length > 0;

  if (isFocusedLayout) {
    return null;
  }

  return (
    <>
      <AppBar
        elevation={0}
        className={classNames(classes.root, {
          [classes.rootMobileFeed]: pathIsMobileFeed,
        })}
      >
        {!isMobile && (
          <Toolbar className={classes.toolbar}>
            <div className={classes.headerColumn}>
              <Breadcrumbs
                classes={{
                  li: classNames(
                    classes.breadcrumbsText,
                    classes.breadcrumbsListItem,
                  ),
                  separator: classes.breadcrumbsText,
                }}
                separator=">"
              >
                {getBreadcrumbs(props, primaryClient, householdId, classes)}
              </Breadcrumbs>
            </div>
            <ClientSearchBar className={classes.headerColumn} />
            <div className={classes.headerColumn}>
              <FDStack direction={'row'} spacing={1}>
                <PlanningVersionLozengePicker resource={householdResource} />
                <EstatePlanningLozenge resource={householdResource} />
                <TaxFilingLozenge resource={householdResource} />
              </FDStack>
              <div className={classes.tags}>
                {householdResource ? householdResource.attr('tags').resources().list().map(tag => <Tag tagResource={tag} />) : null }
              </div>
              <div className={classes.memberAlert}>
                {showAlertButton ? <MemberAlertsTooltip /> : null}
              </div>
              <HeaderActions
                showResources
                clearHousehold={clearHousehold}
                onOutOfOfficeClick={openOutOfOfficeModal}
              />
            </div>
          </Toolbar>
        )}
      </AppBar>
      {(isOutOfOffice && OutOfOfficeBannerComponent) && (
        <OutOfOfficeBannerComponent {...outOfOfficeBannerProps} />
      )}
      {(outOfOfficeModalOpen && OutOfOfficeModalComponent) && (
        <OutOfOfficeModalComponent />
      )}
    </>
  );
};

export default Header;
